import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Download, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Field, Loading, StatusBadge } from "@/components/common";
import { api, errMsg, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];

function SaveBtn({ busy, onClick, testId }) {
  return <Button className="h-11 mt-6" disabled={busy} onClick={onClick} data-testid={testId}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save changes</Button>;
}

function useSaver(path, onDone) {
  const [busy, setBusy] = useState(false);
  const save = async (payload) => {
    setBusy(true);
    try { const { data } = await api.put(path, payload); toast.success("Saved"); onDone(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, save];
}

function BusinessTab({ biz, onSaved }) {
  const { reload } = useAuth();
  const [f, setF] = useState({ name: biz.name, email: biz.email || "", phone: biz.phone || "", address: biz.address || "", timezone: biz.timezone, default_tax_rate: biz.default_tax_rate, invoice_due_days: biz.invoice_due_days });
  const [busy, save] = useSaver("/business", (d) => { onSaved(d); reload(); });
  const zones = useMemo(() => (Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : [biz.timezone]), [biz.timezone]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Panel>
      <div className="grid sm:grid-cols-2 gap-4">
        {[["name", "Business name"], ["email", "Business email (reply-to)"], ["phone", "Phone"], ["address", "Address"]].map(([k, l]) => (
          <Field key={k} label={l} htmlFor={`b-${k}`}><Input id={`b-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-11" data-testid={`business-${k}-input`} /></Field>
        ))}
        <Field label="Time zone" hint="All schedules and recurring jobs use this time zone.">
          <Select value={f.timezone} onValueChange={(v) => set("timezone", v)}><SelectTrigger className="h-11" data-testid="business-timezone-select"><SelectValue /></SelectTrigger><SelectContent className="max-h-72">{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Default tax rate (%)" htmlFor="b-tax"><Input id="b-tax" type="number" min="0" max="30" step="0.01" value={f.default_tax_rate} onChange={(e) => set("default_tax_rate", e.target.value)} className="h-11" data-testid="business-tax-input" /></Field>
        <Field label="Invoice payment terms (days)" htmlFor="b-due"><Input id="b-due" type="number" min="0" value={f.invoice_due_days} onChange={(e) => set("invoice_due_days", e.target.value)} className="h-11" data-testid="business-due-days-input" /></Field>
      </div>
      <SaveBtn busy={busy} testId="save-business-btn" onClick={() => save({ ...f, default_tax_rate: Number(f.default_tax_rate), invoice_due_days: Number(f.invoice_due_days) })} />
    </Panel>
  );
}

function AvailabilityTab({ biz, onSaved }) {
  const [a, setA] = useState(biz.availability);
  const [busy, save] = useSaver("/business/availability", onSaved);
  const set = (d, k, v) => setA((x) => ({ ...x, [d]: { ...x[d], [k]: v } }));
  return (
    <Panel>
      <p className="text-sm text-slate-500 mb-4">Jobs booked outside these hours trigger a warning before saving.</p>
      <div className="space-y-2">
        {DAYS.map(([d, l]) => (
          <div key={d} className="flex flex-wrap items-center gap-3 min-h-[52px] border-b border-slate-100" data-testid={`availability-${d}`}>
            <Switch checked={a[d].enabled} onCheckedChange={(v) => set(d, "enabled", v)} data-testid={`availability-${d}-switch`} />
            <span className="w-28 text-sm font-medium">{l}</span>
            <Input type="time" disabled={!a[d].enabled} value={a[d].start} onChange={(e) => set(d, "start", e.target.value)} className="h-11 w-32" data-testid={`availability-${d}-start`} />
            <span className="text-slate-400">to</span>
            <Input type="time" disabled={!a[d].enabled} value={a[d].end} onChange={(e) => set(d, "end", e.target.value)} className="h-11 w-32" data-testid={`availability-${d}-end`} />
          </div>
        ))}
      </div>
      <SaveBtn busy={busy} testId="save-availability-btn" onClick={() => save(a)} />
    </Panel>
  );
}

function Num({ label, value, onChange, testId, step = "0.01" }) {
  return <Field label={label}><Input type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" data-testid={testId} /></Field>;
}

function PricingTab({ biz, onSaved }) {
  const [p, setP] = useState(biz.pricing);
  const [busy, save] = useSaver("/business/pricing", onSaved);
  const setTop = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setNested = (g, k, v) => setP((x) => ({ ...x, [g]: { ...x[g], [k]: v } }));
  const setExtra = (i, k, v) => setP((x) => ({ ...x, extras: x.extras.map((e, j) => (j === i ? { ...e, [k]: v } : e)) }));
  const toNum = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, Number(v)]));
  const submit = () => save({
    ...p, ...toNum({ per_sqft: p.per_sqft, per_bedroom: p.per_bedroom, per_bathroom: p.per_bathroom, minutes_per_1000_sqft: p.minutes_per_1000_sqft, minutes_per_bedroom: p.minutes_per_bedroom, minutes_per_bathroom: p.minutes_per_bathroom, hourly_target: p.hourly_target, minimum_price: p.minimum_price }),
    base_prices: toNum(p.base_prices), condition_multipliers: toNum(p.condition_multipliers), frequency_discounts: toNum(p.frequency_discounts), time_multipliers: toNum(p.time_multipliers),
    extras: p.extras.map((e) => ({ ...e, key: e.key || e.name.toLowerCase().replace(/[^a-z0-9]+/g, "_"), price: Number(e.price), minutes: Number(e.minutes) })),
  });
  const group = (title, g, labels) => (
    <div><div className="text-sm font-medium text-slate-900 mb-2">{title}</div><div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{labels.map(([k, l]) => <Num key={k} label={l} value={p[g][k]} onChange={(v) => setNested(g, k, v)} testId={`pricing-${g}-${k}`} />)}</div></div>
  );
  return (
    <Panel>
      <div className="space-y-6">
        {group("Base price by cleaning type ($)", "base_prices", [["standard", "Standard"], ["deep", "Deep"], ["move", "Move-in/out"]])}
        <div><div className="text-sm font-medium text-slate-900 mb-2">Size rates ($)</div><div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Num label="Per sq ft" value={p.per_sqft} onChange={(v) => setTop("per_sqft", v)} testId="pricing-per-sqft" />
          <Num label="Per bedroom" value={p.per_bedroom} onChange={(v) => setTop("per_bedroom", v)} testId="pricing-per-bedroom" />
          <Num label="Per bathroom" value={p.per_bathroom} onChange={(v) => setTop("per_bathroom", v)} testId="pricing-per-bathroom" />
        </div></div>
        {group("Condition multipliers (×)", "condition_multipliers", [["light", "Light"], ["average", "Average"], ["heavy", "Heavy"]])}
        {group("Frequency discounts (%)", "frequency_discounts", [["one_time", "One-time"], ["weekly", "Weekly"], ["biweekly", "Biweekly"], ["monthly", "Monthly"]])}
        {group("Labor time multipliers (×)", "time_multipliers", [["standard", "Standard"], ["deep", "Deep"], ["move", "Move-in/out"]])}
        <div><div className="text-sm font-medium text-slate-900 mb-2">Labor & targets</div><div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Num label="Min per 1000 sq ft" value={p.minutes_per_1000_sqft} onChange={(v) => setTop("minutes_per_1000_sqft", v)} testId="pricing-min-sqft" step="1" />
          <Num label="Min per bedroom" value={p.minutes_per_bedroom} onChange={(v) => setTop("minutes_per_bedroom", v)} testId="pricing-min-bedroom" step="1" />
          <Num label="Min per bathroom" value={p.minutes_per_bathroom} onChange={(v) => setTop("minutes_per_bathroom", v)} testId="pricing-min-bathroom" step="1" />
          <Num label="Hourly target ($)" value={p.hourly_target} onChange={(v) => setTop("hourly_target", v)} testId="pricing-hourly-target" />
          <Num label="Minimum price ($)" value={p.minimum_price} onChange={(v) => setTop("minimum_price", v)} testId="pricing-minimum-price" />
        </div></div>
        <div>
          <div className="text-sm font-medium text-slate-900 mb-2">Add-on extras</div>
          {p.extras.map((e, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`pricing-extra-${i}`}>
              <Input className="col-span-12 sm:col-span-6 h-11" placeholder="Name" value={e.name} onChange={(ev) => setExtra(i, "name", ev.target.value)} data-testid={`extra-name-${i}`} />
              <Input className="col-span-5 sm:col-span-2 h-11" type="number" min="0" value={e.price} onChange={(ev) => setExtra(i, "price", ev.target.value)} aria-label="Price" data-testid={`extra-price-${i}`} />
              <Input className="col-span-5 sm:col-span-3 h-11" type="number" min="0" value={e.minutes} onChange={(ev) => setExtra(i, "minutes", ev.target.value)} aria-label="Minutes" data-testid={`extra-minutes-${i}`} />
              <Button variant="ghost" size="icon" className="col-span-2 sm:col-span-1 h-11 w-11" onClick={() => setTop("extras", p.extras.filter((_, j) => j !== i))} aria-label="Remove extra" data-testid={`extra-remove-${i}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => setTop("extras", [...p.extras, { key: "", name: "", price: 0, minutes: 30 }])} data-testid="add-extra-btn"><Plus className="h-4 w-4 mr-1" />Add extra</Button>
          <p className="text-xs text-slate-500 mt-2">Columns: name · price ($) · extra minutes</p>
        </div>
      </div>
      <SaveBtn busy={busy} testId="save-pricing-btn" onClick={submit} />
    </Panel>
  );
}

function MessagesTab() {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    api.get("/notifications").then((r) => setLogs(r.data));
  }, []);
  if (!logs) return <Loading />;
  return (
    <Panel>
      <p className="text-sm text-slate-500 mb-4">Every email and SMS attempt is logged with its real delivery result. SMS is not sent until a Twilio account is connected.</p>
      {logs.length === 0 ? <p className="text-sm text-slate-500">No messages yet.</p> : (
        <div className="divide-y divide-slate-100" data-testid="notification-log">
          {logs.map((l) => (
            <div key={l.id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`log-${l.id}`}>
              <span className="uppercase text-xs font-semibold text-slate-500 w-12">{l.channel}</span>
              <span className="flex-1 min-w-0"><span className="text-slate-900">{l.kind.replace("_", " ")}</span> <span className="text-slate-500">→ {l.to || "—"}</span>{l.error && <span className="block text-xs text-slate-500">{l.error}</span>}</span>
              <span className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</span>
              <StatusBadge status={l.status} />
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function AccountTab() {
  const { user, setSession } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const owner = user.role === "owner";
  const del = async () => {
    setBusy(true);
    try { await api.post("/account/delete", { password: pw, confirm }); setSession(false); navigate("/login"); toast.success("Account deleted"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Panel title="Export your data">
        <p className="text-sm text-slate-500 mb-4">Download every client, job, estimate, invoice, payment and expense in this workspace as JSON.</p>
        <Button variant="outline" className="h-11" disabled={!owner} onClick={() => download("/account/export", "cleanboss-export.json").catch((e) => toast.error(errMsg(e)))} data-testid="export-data-btn"><Download className="h-4 w-4 mr-1" />Export data</Button>
      </Panel>
      <Panel title="Delete account">
        <p className="text-sm text-slate-500 mb-4">Permanently deletes this workspace and all of its data. This cannot be undone.</p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
          <Field label="Your password" htmlFor="del-pw"><Input id="del-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="delete-password-input" /></Field>
          <Field label='Type "DELETE" to confirm' htmlFor="del-c"><Input id="del-c" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" data-testid="delete-confirm-input" /></Field>
        </div>
        <Button className="h-11 mt-4 bg-red-600 hover:bg-red-700 text-white" disabled={!owner || busy || confirm !== "DELETE" || !pw} onClick={del} data-testid="delete-account-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-1" />}Delete account</Button>
      </Panel>
    </div>
  );
}

export default function SettingsPage() {
  const [biz, setBiz] = useState(null);
  useEffect(() => {
    api.get("/business").then((r) => setBiz(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!biz) return <Loading />;
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Plan and payments are managed on the Billing page." />
      <Tabs defaultValue="business">
        <TabsList className="mb-4 flex-wrap h-auto">
          {["business", "availability", "pricing", "messages", "account"].map((t) => <TabsTrigger key={t} value={t} className="capitalize h-9" data-testid={`settings-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="business"><BusinessTab biz={biz} onSaved={setBiz} /></TabsContent>
        <TabsContent value="availability"><AvailabilityTab biz={biz} onSaved={setBiz} /></TabsContent>
        <TabsContent value="pricing"><PricingTab biz={biz} onSaved={setBiz} /></TabsContent>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
        <TabsContent value="account"><AccountTab /></TabsContent>
      </Tabs>
    </div>
  );
}
